/**
 * Session state for the whole app (plan §04.3).
 *
 * Two decisions worth stating, because both are security properties rather
 * than conveniences:
 *
 * **The access token is never persisted** (criterion 16). It lives in
 * `apiClient`'s module scope and dies with the tab. The durable half of the
 * session is the `HttpOnly` refresh cookie, which JavaScript cannot read. So a
 * script injected into the page can make requests while it runs, but cannot
 * take a token away with it.
 *
 * **`hasPermission` is AND, not OR**, mirroring the server's
 * `requirePermission(...keys)`. If the two disagreed, a control would appear
 * for a user the API then refuses — which trains people to distrust the UI.
 * And it is only ever *presentation*: the API is the boundary, and every guard
 * in this folder says so in its own comment because that is the sentence a
 * future reader needs to see before they are tempted to rely on it.
 */

import type {
  BrandingPayload,
  FeatureKey,
  LoginRequest,
  LoginResponse,
  LoginResult,
  MeResponse,
  MeSecurity,
  PermissionKey,
  TenantSummary,
  TotpChallengeResponse,
  TotpVerifyRequest,
  UserSummary,
} from '@rd/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type ReactElement } from 'react';

import { useBranding } from '../branding/BrandingProvider.js';
import {
  apiRequest,
  setAccessToken,
  setTenantRef,
  __resetApiClient,
  ApiClientError,
} from '../lib/apiClient.js';

import { configureRefresh, refreshAccessToken } from './refreshCoordinator.js';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  user: UserSummary | null;
  tenant: TenantSummary | null;
  permissions: ReadonlySet<PermissionKey>;
  features: ReadonlySet<FeatureKey>;
  /** AND semantics — every key must be held. Mirrors `requirePermission()`. */
  hasPermission(...keys: PermissionKey[]): boolean;
  hasFeature(...keys: FeatureKey[]): boolean;
  /** True after login when the password must be changed before anything else. */
  mustChangePassword: boolean;
  /** Set when a refresh failed, so `/login` can explain itself. */
  endedReason: 'session-expired' | null;
  /**
   * §B7: the account-security posture from `/auth/me`, so the shell can steer
   * enrolment. Null until a session exists.
   */
  security: MeSecurity | null;
  /**
   * §B7: login is a union. `null` means the session is established; a
   * `TotpChallengeResponse` means the password was right but the account is
   * TOTP-enrolled, and the caller must collect a code and finish through
   * `verifyTotp` — no tokens exist yet and no session side effects have run.
   */
  login(input: LoginRequest): Promise<TotpChallengeResponse | null>;
  /** The second login step: exchanges challenge + code for a normal session. */
  verifyTotp(input: TotpVerifyRequest): Promise<void>;
  logout(): Promise<void>;
  /** Platform scope only; sets `X-Tenant-Ref` and re-runs `/auth/me`. */
  selectTenant(tenantRef: string | null): Promise<void>;
  refreshMe(): Promise<void>;
  clearMustChangePassword(): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const { adoptBranding } = useBranding();

  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserSummary | null>(null);
  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [permissions, setPermissions] = useState<ReadonlySet<PermissionKey>>(new Set());
  const [features, setFeatures] = useState<ReadonlySet<FeatureKey>>(new Set());
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [endedReason, setEndedReason] = useState<'session-expired' | null>(null);
  const [security, setSecurity] = useState<MeSecurity | null>(null);

  // Read inside callbacks that must not re-subscribe when branding changes.
  const adoptRef = useRef(adoptBranding);
  adoptRef.current = adoptBranding;

  const clearSession = useCallback(() => {
    hadSession.current = false;
    __resetApiClient();
    setUser(null);
    setTenant(null);
    setPermissions(new Set());
    setFeatures(new Set());
    setMustChangePassword(false);
    setSecurity(null);
    setStatus('anonymous');
  }, []);

  /** True once a session has genuinely been established in this tab. */
  const hadSession = useRef(false);

  const applyMe = useCallback((me: MeResponse) => {
    hadSession.current = true;
    setUser(me.user);
    setTenant(me.tenant);
    setPermissions(new Set(me.permissions));
    setFeatures(new Set(me.features));
    setSecurity(me.security ?? null);
    setStatus('authenticated');
    // The authoritative brand for *this* session, which may differ from what
    // the host or `?tenant=` resolved to before login.
    adoptRef.current(me.branding as BrandingPayload);
  }, []);

  const refreshMe = useCallback(async () => {
    const me = await apiRequest<MeResponse>('/auth/me');
    applyMe(me);
  }, [applyMe]);

  /* --- Wire the refresh coordinator once ---------------------------- */
  useEffect(() => {
    configureRefresh({
      perform: async () => {
        // Deliberately NOT `apiRequest`: that would recurse into the 401
        // handling this call is the resolution of.
        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('refresh rejected');
        const body = (await response.json()) as { data: { accessToken: string; expiresIn: number } };
        return body.data;
      },
      onSuccess: (token) => setAccessToken(token),
      onFailure: () => {
        // Read BEFORE clearing — `clearSession()` resets this flag, so testing
        // it afterwards would always be false and the banner would never show.
        const wasSignedIn = hadSession.current;
        clearSession();
        // §04.4: never "token reused" — the user's session ended; how we know
        // that is our business.
        //
        // Only for someone who HAD a session. The boot probe asks the same
        // question of every visitor, and a first-time visitor being told "your
        // session ended" is both untrue and alarming.
        if (wasSignedIn) setEndedReason('session-expired');
      },
    });
  }, [clearSession]);

  /* --- Boot ---------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        /**
         * **Through the coordinator, never a bare `fetch`** (QA defect D5-01).
         *
         * This call used to `fetch('/api/v1/auth/refresh')` directly, which
         * meant two refreshes could be in flight at once — and the refresh
         * token rotates on use. The first rotated and succeeded; the second
         * presented the token the first had just consumed; Phase 01's reuse
         * detection revoked the **entire token family**, correctly, because a
         * replayed refresh token is indistinguishable from a stolen one. The
         * app tripped its own security control and logged the user out on
         * every load with a valid cookie.
         *
         * `refreshCoordinator` exists precisely to prevent that and its own
         * doc comment describes this failure; the bug was that this one caller
         * bypassed it. It is **not** a StrictMode problem — two tabs, or a
         * retry after a blip, do the same thing. StrictMode only made it
         * visible in development, which is StrictMode working as intended.
         */
        await refreshAccessToken();

        const me = await apiRequest<MeResponse>('/auth/me');
        if (!cancelled) applyMe(me);
      } catch {
        if (!cancelled) {
          __resetApiClient();
          setStatus('anonymous');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyMe]);

  /**
   * The one way a login body becomes a session — shared by the plain login
   * and the TOTP second step, so the two paths cannot drift: same token
   * handling, same `/auth/me` round trip, same `mustChangePassword` ordering.
   */
  const completeLogin = useCallback(
    async (result: LoginResponse) => {
      setAccessToken(result.accessToken);
      setEndedReason(null);
      setMustChangePassword(result.mustChangePassword);
      // `/auth/me` rather than trusting the login body alone: it is the one
      // call that carries features and branding, and the shell needs both
      // before it renders anything.
      await refreshMe();
      setMustChangePassword(result.mustChangePassword);
    },
    [refreshMe],
  );

  const login = useCallback(
    async (input: LoginRequest) => {
      const result = await apiRequest<LoginResult>('/auth/login', {
        method: 'POST',
        body: input,
      });
      // B7: login is a union — a TOTP-enrolled user gets a challenge, not
      // tokens. Nothing session-shaped exists yet (no cookie was set), so
      // nothing here may touch session state: the challenge is handed back
      // for the login screen to collect a code and finish via `verifyTotp`.
      // Non-enrolled users never hit this branch and proceed exactly as before.
      if ('challenge' in result) return result;
      await completeLogin(result);
      return null;
    },
    [completeLogin],
  );

  const verifyTotp = useCallback(
    async (input: TotpVerifyRequest) => {
      // The schema is `.strict()` and wants exactly one factor, so the body is
      // built key by key — spreading `input` could carry an `undefined` key a
      // caller left behind, and JSON.stringify would drop it here but a later
      // refactor to FormData or a different serialiser would not.
      const body: TotpVerifyRequest = { challengeToken: input.challengeToken };
      if (input.code !== undefined) body.code = input.code;
      if (input.recoveryCode !== undefined) body.recoveryCode = input.recoveryCode;
      const result = await apiRequest<LoginResponse>('/auth/login/totp', {
        method: 'POST',
        body,
      });
      // Success completes exactly like a normal login: the union's other arm.
      await completeLogin(result);
    },
    [completeLogin],
  );

  const logout = useCallback(async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch {
      // A logout that fails server-side still has to clear the client, or the
      // user is left looking at an account they believe they left.
    }
    clearSession();
    setEndedReason(null);
  }, [clearSession]);

  const selectTenant = useCallback(
    async (ref: string | null) => {
      setTenantRef(ref);
      await refreshMe();
    },
    [refreshMe],
  );

  const hasPermission = useCallback(
    (...keys: PermissionKey[]) => keys.every((k) => permissions.has(k)),
    [permissions],
  );
  const hasFeature = useCallback(
    (...keys: FeatureKey[]) => keys.every((k) => features.has(k)),
    [features],
  );

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      tenant,
      permissions,
      features,
      hasPermission,
      hasFeature,
      mustChangePassword,
      endedReason,
      security,
      login,
      verifyTotp,
      logout,
      selectTenant,
      refreshMe,
      clearMustChangePassword: () => setMustChangePassword(false),
    }),
    [
      status,
      user,
      tenant,
      permissions,
      features,
      hasPermission,
      hasFeature,
      mustChangePassword,
      endedReason,
      security,
      login,
      verifyTotp,
      logout,
      selectTenant,
      refreshMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** True when a thrown error means the password must be changed before anything. */
export function isPasswordExpired(err: unknown): boolean {
  return err instanceof ApiClientError && err.code === 'AUTH_PASSWORD_EXPIRED';
}
